import { decode as _decodeHtml } from "html-entities";
import _balanceText from "../utility/balanceText.js";
import _getLineCount from "../quill/getLineCount.js";
import _convertToHtml from "../quill/convertToHtml.js";
import _convertToPlainTextCustom from "../quill/convertToPlainTextCustom.js";
import _convertToPlainText from "../quill/convertToPlainText.js";
const convertToPlainText = _convertToPlainText;
const convertToPlainTextCustom = _convertToPlainTextCustom;
const convertToHtml = _convertToHtml;
const getLineCount = _getLineCount;
const balanceText = _balanceText;
const decodeHtml = _decodeHtml;

/*
Corrects reading-speed (CPS) and minimum-duration errors in an event group.

Order of operations per event (md-subs "Dealing with High Reading Speed"):
  1. Extend the out-time into the following gap (capped at maxExtendSec past
     the event's original out-time).
  2. Extend the in-time earlier by at most maxInCueFrames.
  3. Borrow time from a neighbor by shortening it, but only if the shortened
     neighbor stays at or under maxCps and at or over minDuration.
  4. Merge with a neighbor (when allowMerge) if the gap between them is at
     most maxMergeGapSec and the merged event passes the CPL (maxChars),
     maxLines, maxDuration and maxCps checks.
  5. Otherwise fail the event: approved = false, and notes is set to
     failureNote when notes are empty (existing notes are never overwritten).

No-cascade invariant: an event's boundaries only ever move within the slack
between its neighbors. Steps 1-2 move only the current event. Step 3 only
SHORTENS a neighbor - the neighbor's far boundary never moves, so nothing
propagates beyond one event. The final frame-gap pass TRIMS the earlier
event's end instead of pushing the later event. Downstream events are never
shifted, so corrections cannot drift the rest of the file out of sync.
*/
export default (function correctReadingSpeed(eventGroup, options = {}) {
  const {
    frameRate,
    maxCps = 25,
    minDuration = 1,
    maxDuration = 12,
    minFrameGap = 2,
    maxExtendSec = 2,
    maxInCueFrames = 6,
    allowMerge = true,
    maxMergeGapSec = 2,
    maxChars = 32,
    maxLines = 2,
    excludeSpaces = false,
    markFailures = true,
    failureNote = "Reading speed too high - could not be corrected automatically"
  } = options;
  if (!frameRate || isNaN(frameRate)) {
    throw new Error("correctReadingSpeed requires a valid frameRate option");
  }
  const EPSILON = 0.0005;
  const frameSec = 1 / Math.round(frameRate);
  const frameGapSec = minFrameGap * frameSec;
  const maxInShift = maxInCueFrames * frameSec;
  const events = eventGroup.events;
  const stats = {
    checked: 0,
    extended: 0,
    merged: 0,
    failed: 0,
    unchanged: 0
  };
  function countChars(html) {
    if (!html) {
      return 0;
    }
    const plain = convertToPlainText(html);
    if (!plain.trim()) {
      return 0;
    }
    return plain.split("\n").reduce((sum, line) => {
      let content = decodeHtml(line);
      if (excludeSpaces) {
        content = content.replace(/ /g, "");
      }
      return sum + content.length;
    }, 0);
  }

  /* Minimum duration this event needs to satisfy both maxCps and minDuration */
  function neededDuration(ev) {
    const chars = countChars(ev.text);
    return Math.max(chars > 0 ? chars / maxCps : 0, minDuration);
  }
  function isValid(ev) {
    return typeof ev.start === "number" && typeof ev.end === "number" && !isNaN(ev.start) && !isNaN(ev.end);
  }

  /* a is the earlier event. Returns { html } when the merge passes all
     checks, or null when merging is impossible. */
  function canMerge(a, b) {
    const gap = b.start - a.end;
    if (gap > maxMergeGapSec) {
      return null;
    }
    const mergedDur = b.end - a.start;
    if (mergedDur <= 0 || mergedDur > maxDuration) {
      return null;
    }
    const plain = (convertToPlainTextCustom(a.text || "") + "\n" + convertToPlainTextCustom(b.text || "")).trim();
    let html = convertToHtml(plain);
    if (countChars(html) / mergedDur > maxCps + EPSILON) {
      return null;
    }
    const cplOk = h => convertToPlainText(h).split("\n").every(l => l.length <= maxChars);
    if (getLineCount(html) > maxLines || !cplOk(html)) {
      const lines = balanceText(html, maxChars, maxLines);
      if (lines.length > maxLines || lines.some(l => convertToPlainText(l, " ").length > maxChars)) {
        return null;
      }
      html = convertToHtml(lines.join("\n"));
    }
    return {
      html
    };
  }

  /* Merge events[secondIdx] into events[firstIdx] (Merge.svelte parity) */
  function doMerge(firstIdx, secondIdx, mergedHtml) {
    const first = events[firstIdx];
    const second = events[secondIdx];
    first.text = mergedHtml;
    first.end = second.end;
    if (first.original || second.original) {
      first.original = convertToHtml((convertToPlainTextCustom(first.original || "") + "\n" + convertToPlainTextCustom(second.original || "")).trim());
    }
    const speakerIds = new Set();
    first.speakers = [...(first.speakers || []), ...(second.speakers || [])].filter(speaker => {
      if (!speaker || !speaker.id || speakerIds.has(speaker.id)) {
        return false;
      }
      speakerIds.add(speaker.id);
      return true;
    });
    first.rendered = false;
    origEnd.set(first.id, origEnd.get(second.id) !== undefined ? origEnd.get(second.id) : second.end);
    events.splice(secondIdx, 1);
  }

  /* Extension caps are measured against the event's ORIGINAL out-time so
     partial adjustments and merges cannot creep past maxExtendSec. Keyed
     by id because merges splice the array. */
  const origEnd = new Map();
  events.forEach(ev => origEnd.set(ev.id, ev.end));
  let i = 0;
  let guard = events.length * 4;
  while (i < events.length && guard-- > 0) {
    const ev = events[i];
    if (!isValid(ev)) {
      i++;
      continue;
    }

    /* Over-long events are trimmed locally; if the trim pushes them over
       maxCps they fall through the extend/merge/fail pipeline below */
    if (ev.end - ev.start > maxDuration) {
      ev.end = ev.start + maxDuration;
    }
    const needed = neededDuration(ev);
    if (ev.end - ev.start >= needed - EPSILON) {
      stats.unchanged++;
      i++;
      continue;
    }
    stats.checked++;
    const prev = i > 0 ? events[i - 1] : null;
    const next = i < events.length - 1 ? events[i + 1] : null;
    const startAtProcessing = ev.start;

    /* The out-time may never pass this point, no matter where the time
       comes from (gap or borrowing) */
    const endCap = Math.min((origEnd.get(ev.id) !== undefined ? origEnd.get(ev.id) : ev.end) + maxExtendSec, ev.start + maxDuration);
    const targetEnd = Math.min(ev.start + needed, endCap);

    /* Step 1: extend out-time into the following gap */
    const nextLimit = next && isValid(next) ? next.start - frameGapSec : Infinity;
    const grownEnd = Math.min(targetEnd, nextLimit);
    if (grownEnd > ev.end) {
      ev.end = grownEnd;
    }

    /* Step 2: extend in-time earlier, a few frames at most */
    if (ev.end - ev.start < needed - EPSILON) {
      const startFloor = Math.max(prev && isValid(prev) ? prev.end + frameGapSec : 0, startAtProcessing - maxInShift, 0);
      const newStart = Math.max(ev.end - needed, startFloor);
      if (newStart < ev.start) {
        ev.start = newStart;
      }
    }

    /* Step 3: borrow from the next event by delaying its in-time, as
       long as it stays compliant. Partial borrows are allowed. */
    if (ev.end - ev.start < needed - EPSILON && next && isValid(next) && ev.end < targetEnd) {
      const nextChars = countChars(next.text);
      const nextMinDur = Math.max(minDuration, nextChars > 0 ? nextChars / maxCps : 0);
      const latestNextStart = next.end - nextMinDur;
      const newNextStart = Math.min(targetEnd + frameGapSec, latestNextStart);
      if (newNextStart > next.start + EPSILON) {
        next.start = newNextStart;
        ev.end = Math.min(targetEnd, newNextStart - frameGapSec);
      }
    }

    /* Step 3b: borrow from the previous event by pulling its out-time
       earlier - still bounded by our own in-cue budget */
    if (ev.end - ev.start < needed - EPSILON && prev && isValid(prev)) {
      const prevChars = countChars(prev.text);
      const prevMinDur = Math.max(minDuration, prevChars > 0 ? prevChars / maxCps : 0);
      const earliestPrevEnd = prev.start + prevMinDur;
      const inCueFloor = Math.max(startAtProcessing - maxInShift, 0);
      const desiredStart = Math.max(ev.end - needed, inCueFloor);
      const newPrevEnd = Math.max(desiredStart - frameGapSec, earliestPrevEnd);
      const newStart = Math.max(desiredStart, newPrevEnd + frameGapSec);
      if (newPrevEnd < prev.end - EPSILON && newStart < ev.start - EPSILON) {
        prev.end = newPrevEnd;
        ev.start = newStart;
      }
    }
    if (ev.end - ev.start >= needed - EPSILON) {
      stats.extended++;
      i++;
      continue;
    }

    /* Step 4: merge with a neighbor. Prefer the candidate with the
       smaller gap; ties go to the next event. */
    if (allowMerge) {
      const candNext = next && isValid(next) ? canMerge(ev, next) : null;
      const candPrev = prev && isValid(prev) ? canMerge(prev, ev) : null;
      if (candNext || candPrev) {
        let useNext = Boolean(candNext);
        if (candNext && candPrev) {
          useNext = next.start - ev.end <= ev.start - prev.end;
        }
        if (useNext) {
          doMerge(i, i + 1, candNext.html);
          stats.merged++;
          continue; /* re-evaluate the merged event at the same index */
        } else {
          doMerge(i - 1, i, candPrev.html);
          stats.merged++;
          i--; /* re-evaluate the merged event */
          continue;
        }
      }
    }

    /* Step 5: fail the event */
    stats.failed++;
    if (markFailures) {
      ev.approved = false;
      if (!ev.notes || !ev.notes.trim()) {
        ev.notes = failureNote;
      }
    }
    i++;
  }

  /* Frame-gap enforcement: trim the earlier event's end. The later event
     is never pushed, so gaps can't accumulate into drift. */
  for (let k = 0; k < events.length - 1; k++) {
    const a = events[k];
    const b = events[k + 1];
    if (!isValid(a) || !isValid(b)) {
      continue;
    }
    if (b.start - a.end < frameGapSec - EPSILON) {
      a.end = Math.max(b.start - frameGapSec, a.start + frameSec);
    }
  }
  return {
    eventGroup,
    stats
  };
});